import { useEffect, useMemo, useState } from "react";
import parse from "html-react-parser";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import API from "../../API";
import CmModal from "../Controls/CmModal";
import CustomButton from "../Controls/CustomButton";
import {
  decodeAgreementHtml,
  encodeAgreementHtml,
} from "../Controls/agreementContent";
import { useModalBusy } from "../Controls/modalBusy";

function renderHtml(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (/<\/?[a-z][\s\S]*>/i.test(raw)) return parse(raw);
  return <pre className="agreementContent">{raw}</pre>;
}

/**
 * Admin agreement detail: view current version, toggle RequiredForLogin, open Add version.
 */
export default function AdminAgreementDetailsModal({
  agreement,
  onClose,
  onUpdated,
}) {
  const [addOpen, setAddOpen] = useState(false);

  const title = agreement?.Name || agreement?.name || "Agreement Details";

  return (
    <>
      <CmModal
        open
        onClose={onClose}
        title={title}
        size="wide"
        testId="admin-agreement-details-modal"
        className="agreement-details-modal"
        footer={(close) => (
          <>
            <CustomButton type="button" variant="outlined" onClick={close}>
              Close
            </CustomButton>
            <CustomButton
              type="button"
              variant="contained"
              color="primary"
              onClick={() => setAddOpen(true)}
              data-testid="admin-agreement-add-version"
            >
              Add
            </CustomButton>
          </>
        )}
      >
        {!agreement ? (
          <p>Agreement not found.</p>
        ) : (
          <DetailsBody agreement={agreement} onUpdated={onUpdated} />
        )}
      </CmModal>

      {addOpen ? (
        <AdminAgreementVersionModal
          base={agreement}
          onClose={() => setAddOpen(false)}
          onCreated={(created) => {
            setAddOpen(false);
            onUpdated?.(created);
            onClose?.();
          }}
        />
      ) : null}
    </>
  );
}

function DetailsBody({ agreement, onUpdated }) {
  const [requiredForLogin, setRequiredForLogin] = useState(
    !!(agreement?.RequiredForLogin ?? agreement?.requiredForLogin)
  );
  const { busy, withBusy } = useModalBusy();

  useEffect(() => {
    setRequiredForLogin(
      !!(agreement?.RequiredForLogin ?? agreement?.requiredForLogin)
    );
  }, [agreement]);

  const html = useMemo(() => {
    const raw = agreement?.Content || agreement?.content || "";
    return decodeAgreementHtml(raw);
  }, [agreement]);

  const typeName = agreement?.TypeName || agreement?.typeName || "";
  const version =
    agreement?.Version ||
    `${agreement?.MajorVersion ?? 0}.${agreement?.MinorVersion ?? 0}`;
  const typeId = agreement?.TypeID ?? agreement?.typeID;
  const activeDate = agreement?.ActiveDate
    ? DateTime.fromISO(agreement.ActiveDate).toFormat("MM/dd/yyyy")
    : "—";
  const killDate = agreement?.KillDate
    ? DateTime.fromISO(agreement.KillDate).toFormat("MM/dd/yyyy")
    : "Open";

  async function onToggleRequired(e) {
    const next = e.target.checked;
    setRequiredForLogin(next);
    await withBusy(async () => {
      try {
        const resp = await API.patchAsync(`/AgreementTypes/${typeId}`, {
          RequiredForLogin: next,
        });
        if (resp?.ok) {
          toast.success("Required for login updated");
          onUpdated?.({ ...agreement, RequiredForLogin: next });
        } else {
          setRequiredForLogin(!next);
          toast.error(resp?.jsonData?.message || "Could not update type");
        }
      } catch (err) {
        setRequiredForLogin(!next);
        toast.error(err?.message || "Could not update type");
      }
    });
  }

  return (
    <div>
      <p>
        <strong>Type:</strong> {typeName || typeId}
      </p>
      <p>
        <strong>Version:</strong> {version}
      </p>
      <p>
        <strong>Effective:</strong> {activeDate} — {killDate}
      </p>
      <label className="form-label" style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="checkbox"
          checked={requiredForLogin}
          disabled={busy || typeId == null}
          onChange={onToggleRequired}
          data-testid="admin-required-for-login"
        />
        Required for login
      </label>
      <div className="agreementContent" style={{ marginTop: 16 }}>
        {renderHtml(html)}
      </div>
    </div>
  );
}

function AdminAgreementVersionModal({ base, onClose, onCreated }) {
  const major = base?.MajorVersion ?? 1;
  const nextMinor = (base?.MinorVersion ?? 0) + 1;
  const [name, setName] = useState(base?.Name || base?.name || "");
  const [majorVersion, setMajorVersion] = useState(major);
  const [minorVersion, setMinorVersion] = useState(nextMinor);
  const [activeDate, setActiveDate] = useState(
    DateTime.utc().toISODate() || ""
  );
  const [html, setHtml] = useState(() =>
    decodeAgreementHtml(base?.Content || base?.content || "")
  );

  async function saveVersion() {
    if (!html.trim()) {
      toast.error("HTML content is required");
      return;
    }
    try {
      const resp = await API.postAsync("/Agreements/Version", {
        TypeID: base?.TypeID ?? base?.typeID,
        MajorVersion: Number(majorVersion),
        MinorVersion: Number(minorVersion),
        Name: name,
        Content: encodeAgreementHtml(html),
        ActiveDate: DateTime.fromISO(activeDate, { zone: "utc" }).toISO(),
      });
      if (resp?.ok) {
        toast.success("New version published");
        onCreated?.(resp.jsonData);
      } else {
        toast.error(resp?.jsonData?.message || "Could not create version");
      }
    } catch (err) {
      toast.error(err?.message || "Could not create version");
    }
  }

  return (
    <CmModal
      open
      onClose={onClose}
      title="Add agreement version"
      size="xl"
      testId="admin-agreement-version-modal"
      footer={(close) => (
        <VersionFooter close={close} onSave={saveVersion} />
      )}
    >
      <VersionBody
        base={base}
        name={name}
        setName={setName}
        majorVersion={majorVersion}
        setMajorVersion={setMajorVersion}
        minorVersion={minorVersion}
        setMinorVersion={setMinorVersion}
        activeDate={activeDate}
        setActiveDate={setActiveDate}
        html={html}
        setHtml={setHtml}
      />
    </CmModal>
  );
}

function VersionFooter({ close, onSave }) {
  const { busy, withBusy } = useModalBusy();

  function handleSave() {
    void withBusy(onSave).catch((err) => console.error(err));
  }

  return (
    <>
      <CustomButton type="button" variant="outlined" onClick={close}>
        Cancel
      </CustomButton>
      <CustomButton
        type="button"
        variant="contained"
        color="primary"
        disabled={busy}
        onClick={handleSave}
        data-testid="admin-agreement-version-save"
      >
        {busy ? "Saving…" : "Publish"}
      </CustomButton>
    </>
  );
}

function VersionBody({
  base,
  name,
  setName,
  majorVersion,
  setMajorVersion,
  minorVersion,
  setMinorVersion,
  activeDate,
  setActiveDate,
  html,
  setHtml,
}) {
  return (
    <div className="cm-modal-body-pad">
      <p className="fieldHint">
        Type: {base?.TypeName || base?.typeName || base?.TypeID} (read-only)
      </p>
      <div className="field form-group">
        <label className="form-label" htmlFor="agr-ver-name">
          Name
        </label>
        <input
          id="agr-ver-name"
          className="form-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <div className="field form-group" style={{ flex: 1 }}>
          <label className="form-label" htmlFor="agr-ver-major">
            Major
          </label>
          <input
            id="agr-ver-major"
            type="number"
            className="form-input"
            value={majorVersion}
            onChange={(e) => setMajorVersion(e.target.value)}
          />
        </div>
        <div className="field form-group" style={{ flex: 1 }}>
          <label className="form-label" htmlFor="agr-ver-minor">
            Minor
          </label>
          <input
            id="agr-ver-minor"
            type="number"
            className="form-input"
            value={minorVersion}
            onChange={(e) => setMinorVersion(e.target.value)}
          />
        </div>
        <div className="field form-group" style={{ flex: 2 }}>
          <label className="form-label" htmlFor="agr-ver-active">
            Effective date (UTC)
          </label>
          <input
            id="agr-ver-active"
            type="date"
            className="form-input"
            value={activeDate}
            onChange={(e) => setActiveDate(e.target.value)}
          />
        </div>
      </div>
      <div
        className="agreement-html-split"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          minHeight: 280,
          marginTop: 12,
        }}
      >
        <div>
          <label className="form-label" htmlFor="agr-ver-html">
            HTML
          </label>
          <textarea
            id="agr-ver-html"
            className="form-input"
            style={{ width: "100%", minHeight: 260, fontFamily: "monospace" }}
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            data-testid="admin-agreement-html-editor"
          />
        </div>
        <div>
          <label className="form-label">Preview</label>
          <div
            className="agreementContent"
            style={{
              border: "1px solid var(--cm-border, #ccc)",
              borderRadius: 4,
              padding: 12,
              minHeight: 260,
              overflow: "auto",
            }}
            data-testid="admin-agreement-html-preview"
          >
            {renderHtml(html)}
          </div>
        </div>
      </div>
    </div>
  );
}
